import { createRouter, createWebHistory } from '@ionic/vue-router'
import { watch } from 'vue'
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreReviews } from '@/stores/storeReviews'
import { useStorePreCache } from '@/stores/storePreCache'
import { Capacitor } from '@capacitor/core'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { getRouteComponent } from './routeComponents'

let storeAuth = null
let storeProfile = null
let storePreCache = null

function getAppMode() {
  const platform = Capacitor.getPlatform()

  if (platform === 'ios' || platform === 'android') {
    return 'native_app'
  } else {
    return 'web'
  }
}

const routes = [
  {
    path: '/',
    redirect: () => {
      const appMode = getAppMode()
      if (appMode === 'native_app') {
        return { name: 'WebIndex' }
      } else {
        return '/public'
      }
    }
  },

  {
    path: '/explore',
    redirect: { name: 'WebIndex' }
  },

  {
    path: '/public',
    component: getRouteComponent('LayoutV2'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        component: getRouteComponent('LayoutPublic'),
        meta: { requiresAuth: false, requiresAccess: false },
        beforeEnter(to, from, next) {
          next()
        },
        children: [
          {
            path: '',
            name: 'PublicIndex',
            component: getRouteComponent('ViewPublicIndex')
          },
          {
            path: 'whats-new',
            name: 'PublicWhatsNew',
            component: getRouteComponent('ViewPublicWhatsNew')
          },
          {
            path: 'title',
            name: 'PublicTitle',
            component: getRouteComponent('ViewPublic')
          }
        ]
      }
    ]
  },

  {
    path: '/~',
    component: getRouteComponent('LayoutWeb'),
    meta: { requiresAuth: false },
    children: [
      {
        path: '',
        redirect: { name: 'WebIndex' }
      },

      // ============================================
      // EXPLORE TAB STACK
      // Routes: /~/explore, /~/explore/title/..., etc.
      // ============================================
      {
        path: 'explore',
        name: 'WebIndex',
        component: getRouteComponent('ViewWebIndex'),
        meta: { requiresAccess: false, requiresAuth: false, tab: 'explore' }
      },
      {
        path: 'explore/title/:type/:id/:view(overview|seasons|cast|reviews|filmography)?/:season?',
        name: 'ExploreTitle',
        component: getRouteComponent('ViewTitle'),
        meta: { requiresAuth: false, tab: 'explore' },
        props: (route) => ({
          type: route.params.type,
          id: route.params.id,
          view: route.params.view || route.query.view || 'overview',
          season: route.params.season || null,
          episode: route.params.episode || null
        })
      },
      {
        path: 'explore/trending',
        name: 'ExploreTrending',
        component: getRouteComponent('ViewWebTrending'),
        meta: { requiresAuth: false, tab: 'explore' }
      },

      // ============================================
      // FEED TAB STACK
      // Routes: /~/feed, /~/feed/review/..., etc.
      // Also handles deep-linked reviews from notifications
      // ============================================
      {
        path: 'feed',
        name: 'WebFeed',
        component: getRouteComponent('ViewWebFeed'),
        meta: { requiresAuth: false, tab: 'feed' }
      },
      {
        path: 'feed/activity',
        name: 'WebActivity',
        component: getRouteComponent('ViewWebActivity'),
        meta: { requiresAuth: false, tab: 'feed' }
      },
      {
        path: 'feed/review/:id',
        name: 'FeedReviewSingle',
        component: getRouteComponent('ViewWebReviewsDetail'),
        meta: { requiresAccess: false, requiresAuth: false, tab: 'feed' },
        beforeEnter: (to, from, next) => {
          useStoreReviews()
            .fetchReviewById(to.params.id)
            .then(() => {
              next()
            })
        }
      },
      {
        path: 'feed/title/:type/:id/:view(overview|seasons|cast|reviews|filmography)?/:season?',
        name: 'FeedTitle',
        component: getRouteComponent('ViewTitle'),
        meta: { requiresAuth: false, tab: 'feed' },
        props: (route) => ({
          type: route.params.type,
          id: route.params.id,
          view: route.params.view || route.query.view || 'overview',
          season: route.params.season || null,
          episode: route.params.episode || null
        })
      },
      {
        path: 'feed/user/@:username',
        name: 'FeedUser',
        component: getRouteComponent('ViewWebUser'),
        meta: { requiresAuth: false, tab: 'feed' },
        beforeEnter: (to, from, next) => {
          useStoreProfile().fetchUserProfileByUsername(to.params.username)
          next()
        }
      },

      // ============================================
      // LISTS TAB STACK
      // Routes: /~/lists, /~/lists/:id, etc.
      // ============================================
      {
        path: 'lists',
        name: 'WebLists',
        component: getRouteComponent('ViewWebAllLists'),
        meta: { requiresAuth: false, tab: 'lists' }
      },
      {
        path: 'lists/join/:inviteCode',
        name: 'WebListJoin',
        component: getRouteComponent('ViewWebListJoin'),
        meta: { requiresAuth: true, tab: 'lists' }
      },
      {
        path: 'lists/:listId',
        name: 'WebListSingle',
        component: getRouteComponent('ViewWebUserListSingle'),
        meta: { requiresAuth: false, tab: 'lists' }
      },
      {
        path: 'lists/:listId/title/:type/:id/:view(overview|seasons|cast|reviews|filmography)?/:season?',
        name: 'ListsTitle',
        component: getRouteComponent('ViewTitle'),
        meta: { requiresAuth: false, tab: 'lists' },
        props: (route) => ({
          type: route.params.type,
          id: route.params.id,
          view: route.params.view || route.query.view || 'overview',
          season: route.params.season || null,
          episode: route.params.episode || null
        })
      },
      {
        path: 'lists/user/@:username',
        name: 'ListsUserLists',
        component: getRouteComponent('ViewWebUserLists'),
        props: (route) => ({ username: route.params.username }),
        meta: { requiresAuth: false, tab: 'lists' },
        beforeEnter: (to, from, next) => {
          useStoreProfile().fetchUserProfileByUsername(to.params.username)
          next()
        }
      },
      {
        path: 'lists/user/@:username/:listId',
        name: 'ListsUserListSingle',
        component: getRouteComponent('ViewWebUserListSingle'),
        props: (route) => ({ username: route.params.username }),
        meta: { requiresAuth: false, tab: 'lists' }
      },

      // ============================================
      // CLUBS TAB STACK
      // Routes: /~/clubs, /~/clubs/:slug, etc.
      // ============================================
      {
        path: 'clubs',
        name: 'WebClubs',
        component: getRouteComponent('ViewWebClubs'),
        meta: { requiresAuth: false, tab: 'clubs' }
      },
      {
        path: 'clubs/new',
        name: 'WebClubCreate',
        component: getRouteComponent('ViewWebClubCreate'),
        meta: { requiresAuth: true, tab: 'clubs' }
      },
      {
        path: 'clubs/join/:inviteCode',
        name: 'WebClubJoin',
        component: getRouteComponent('ViewWebClubJoin'),
        meta: { requiresAuth: true, tab: 'clubs' }
      },
      {
        path: 'clubs/:slug',
        name: 'WebClubDetail',
        component: getRouteComponent('ViewWebClubDetail'),
        meta: { requiresAuth: false, tab: 'clubs' }
      },
      {
        path: 'clubs/:slug/settings',
        name: 'WebClubSettings',
        redirect: (to) => ({
          name: 'WebClubAdmin',
          params: { slug: to.params.slug },
          query: { tab: 'settings' }
        })
      },
      {
        path: 'clubs/:slug/admin',
        name: 'WebClubAdmin',
        component: getRouteComponent('ViewWebClubAdmin'),
        meta: { requiresAuth: true, tab: 'clubs' }
      },
      {
        path: 'clubs/:slug/title/:type/:id/:view(overview|seasons|cast|reviews|filmography)?/:season?',
        name: 'ClubsTitle',
        component: getRouteComponent('ViewTitle'),
        meta: { requiresAuth: false, tab: 'clubs' },
        props: (route) => ({
          type: route.params.type,
          id: route.params.id,
          view: route.params.view || route.query.view || 'overview',
          season: route.params.season || null,
          episode: route.params.episode || null
        })
      },

      // ============================================
      // SEARCH TAB STACK
      // Routes: /~/search, /~/search/title/..., etc.
      // ============================================
      {
        path: 'search',
        name: 'WebSearch',
        component: getRouteComponent('ViewSearch'),
        meta: { requiresAuth: false, tab: 'search' }
      },
      {
        path: 'search/title/:type/:id/:view(overview|seasons|cast|reviews|filmography)?/:season?',
        name: 'SearchTitle',
        component: getRouteComponent('ViewTitle'),
        meta: { requiresAuth: false, tab: 'search' },
        props: (route) => ({
          type: route.params.type,
          id: route.params.id,
          view: route.params.view || route.query.view || 'overview',
          season: route.params.season || null,
          episode: route.params.episode || null
        })
      },
      {
        path: 'lists/user-invite/:inviteId',
        name: 'WebListUserInvite',
        component: getRouteComponent('ViewWebListUserInvite'),
        meta: { requiresAuth: true }
      },
      {
        path: '@:username/lists',
        name: 'WebUserList',
        component: getRouteComponent('ViewWebUserLists'), // Page for user-specific lists
        props: (route) => ({ username: route.params.username }), // Pass username as a prop to the component
        meta: { requiresAuth: false }
      },
      {
        path: '@:username/:listId',
        name: 'WebUserListSingle',
        component: getRouteComponent('ViewWebUserLists'), // Page for user-specific lists
        props: (route) => ({ username: route.params.username }), // Pass username as a prop to the component
        meta: { requiresAuth: false }
      },
      {
        path: 'feed',
        name: 'WebFeed',
        component: getRouteComponent('ViewWebFeed'),
        meta: { requiresAuth: false }
      },
      {
        path: 'activity',
        name: 'WebActivity',
        component: getRouteComponent('ViewWebActivity'),
        meta: { requiresAuth: false }
      },
      {
        path: 'search/user/@:username',
        name: 'SearchUser',
        component: getRouteComponent('ViewWebUser'),
        meta: { requiresAuth: false, tab: 'search' },
        beforeEnter: (to, from, next) => {
          useStoreProfile().fetchUserProfileByUsername(to.params.username)
          next()
        }
      },

      // ============================================
      // SHARED ROUTES (Not tab-specific)
      // ============================================
      {
        path: 'notifications',
        name: 'WebNotifications',
        component: getRouteComponent('ViewWebNotifications'),
        meta: { requiresAuth: true }
      },
      {
        path: 'profile',
        name: 'WebProfile',
        component: getRouteComponent('ViewWebProfile'),
        meta: { requiresAuth: false },
        beforeEnter: (to, from, next) => {
          const storeAuth = useStoreAuth()
          if (storeAuth.user) {
            useStoreProfile().fetchCurrentUserProfile()
          }
          next()
        }
      },
      {
        path: 'profile/edit',
        name: 'WebProfileEdit',
        component: getRouteComponent('ViewWebProfileEdit'),
        meta: { requiresAuth: true }
      },
      {
        path: 'settings',
        name: 'WebSettings',
        component: getRouteComponent('ViewSettings'),
        meta: { requiresAuth: false }
      },
      {
        path: 'settings/advanced',
        name: 'WebSettingsAdvanced',
        component: getRouteComponent('ViewSettingsAdvanced'),
        meta: { requiresAuth: false }
      },
      {
        path: 'settings/notifications',
        name: 'WebSettingsNotifications',
        component: getRouteComponent('ViewSettingsNotifications'),
        meta: { requiresAuth: true }
      },
      {
        path: 'settings/import',
        name: 'WebSettingsImport',
        component: getRouteComponent('ViewSettingsImport'),
        meta: { requiresAuth: true }
      },
      {
        path: 'preferences',
        name: 'WebPreferences',
        component: getRouteComponent('ViewWebPreferences'),
        meta: { requiresAuth: true }
      },
      {
        path: 'home-customize',
        name: 'WebHomeCustomization',
        component: getRouteComponent('ViewHomeCustomization'),
        meta: { requiresAuth: true }
      },
      {
        path: 'people',
        name: 'WebPeople',
        component: getRouteComponent('ViewWebPeople'),
        meta: { requiresAuth: true }
      },
      {
        path: 'circle',
        name: 'WebCircle',
        component: getRouteComponent('ViewCircle'),
        meta: { requiresAuth: true }
      },
      {
        path: 'l/:slug(circle|reviews|recents|watchlist|favourites|seen)',
        name: 'WebUserListSystem',
        component: getRouteComponent('ViewWebUserListSystem'),
        meta: { requiresAuth: true }
      },
      {
        path: 'admin',
        name: 'WebAdmin',
        component: getRouteComponent('ViewWebAdmin'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin-bounce',
        name: 'WebAdminBounce',
        component: getRouteComponent('ViewWebAdminBounce'),
        meta: { requiresAdmin: false }
      },

      // ============================================
      // LEGACY ROUTES (kept for component compatibility)
      // These are real routes because components use :to bindings
      // with route names, which don't work with redirects
      // ============================================
      {
        path: 'title/:type/:id/:view(overview|seasons|cast|reviews|filmography)?/:season?',
        name: 'WebTitle',
        component: getRouteComponent('ViewTitle'),
        meta: { requiresAuth: false, tab: 'explore' },
        props: (route) => ({
          type: route.params.type,
          id: route.params.id,
          view: route.params.view || route.query.view || 'overview',
          season: route.params.season || null,
          episode: route.params.episode || null
        })
      },
      {
        path: 'view/:id',
        name: 'WebTitleDirect',
        component: getRouteComponent('ViewTitle'),
        meta: { requiresAuth: false, tab: 'explore' }
      },
      {
        path: 'trending',
        name: 'WebTrending',
        component: getRouteComponent('ViewWebTrending'),
        meta: { requiresAuth: false, tab: 'explore' }
      },
      // Old activity route -> redirect to feed tab
      {
        path: 'activity',
        redirect: { name: 'WebActivity' }
      },
      // Old review routes (kept for component compatibility)
      {
        path: 'reviews/view/:id',
        name: 'WebReviewsSingle',
        component: getRouteComponent('ViewWebReviewsDetail'),
        meta: { requiresAccess: false, requiresAuth: false, tab: 'feed' },
        beforeEnter: (to, from, next) => {
          useStoreReviews()
            .fetchReviewById(to.params.id)
            .then(() => {
              next()
            })
        }
      },
      {
        path: 'reviews/:id/shared',
        name: 'WebReviewShared',
        component: () => import('@/views/v2/ViewWebReviewShared'),
        meta: { requiresAuth: false },
        beforeEnter: (to, from, next) => {
          const storeReviews = useStoreReviews()
          storeReviews
            .fetchReviewById(to.params.id)
            .then(() => {
              next()
            })
            .catch((error) => {
              console.error('Failed to fetch review:', error)
              next(false)
            })
        }
      },
      {
        path: 'reviews/new',
        name: 'WebRewiewsNew',
        component: () => import('@/views/v2/ViewWebReviewsNew'),
        meta: { requiresAuth: true }
      },
      {
        path: 'reviews/public',
        name: 'WebPublicReviews',
        component: () => import('@/views/v2/ViewWebPublicReviews'),
        meta: { requiresAuth: false }
      },
      {
        name: 'WebReviewsIndex',
        path: 'reviews',
        redirect: { name: 'WebFeed' }
      },
      // ============================================
      // LEGACY @ ROUTES (kept for component compatibility)
      // These are real routes (not redirects) because components
      // use :to bindings with route names, which don't work with redirects
      // ============================================
      {
        // User profile by ID - renders directly without redirect to preserve navigation history
        // Used by feed items for stable links that survive username changes
        path: 'user/:userId',
        name: 'WebUserById',
        component: getRouteComponent('ViewWebUser'),
        meta: { requiresAuth: false },
        beforeEnter: async (to, from, next) => {
          try {
            const profile = await useStoreProfile().fetchProfileById(
              to.params.userId
            )
            if (profile) {
              next()
            } else {
              // Profile not found, go to home
              next({ name: 'WebIndex' })
            }
          } catch (error) {
            console.error('Failed to resolve user by ID:', error)
            next({ name: 'WebIndex' })
          }
        }
      },
      {
        path: '@:username',
        name: 'WebUser',
        component: getRouteComponent('ViewWebUser'),
        meta: { requiresAuth: false, tab: 'feed' },
        beforeEnter: (to, from, next) => {
          useStoreProfile().fetchUserProfileByUsername(to.params.username)
          next()
        }
      },
      {
        path: '@:username/lists',
        name: 'WebUserLists',
        component: getRouteComponent('ViewWebUserLists'),
        props: (route) => ({ username: route.params.username }),
        meta: { requiresAuth: false, tab: 'lists' },
        beforeEnter: (to, from, next) => {
          useStoreProfile().fetchUserProfileByUsername(to.params.username)
          next()
        }
      },
      {
        path: '@:username/lists/:listId',
        name: 'WebUserListSingle',
        component: getRouteComponent('ViewWebUserListSingle'),
        props: (route) => ({ username: route.params.username }),
        meta: { requiresAuth: false, tab: 'lists' }
      },
      {
        path: '@:username/:listId',
        name: 'WebUserListSlug',
        component: getRouteComponent('ViewWebUserListSingle'),
        props: (route) => ({ username: route.params.username }),
        meta: { requiresAuth: false, tab: 'lists' }
      }
    ]
  },
  {
    path: '/auth/magic-link',
    name: 'auth-magic-link',
    component: () => import('@/views/ViewAuthMagicLink'),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      requiresAuth: false
    },
    beforeEnter() {
      useStoreAuth()
        .clearSession()
        .then(() => {
          window.location.href = '/'
        })
      return false
    }
  },
  {
    path: '/auth',
    name: 'auth',
    component: () => import('@/views/ViewAuth'),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/ViewRegister'),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/invited/:code',
    name: 'InviteLanding',
    component: () => import('@/views/v2/ViewWebInviteLanding'),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/ViewPrivacyPolicy'),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/ViewTermsOfService'),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/support',
    name: 'support',
    component: () => import('@/views/ViewSupport'),
    meta: {
      requiresAuth: false
    }
  },

  // Testers page for app reviewers (web only - not linked in mobile app)
  {
    path: '/testers',
    name: 'testers',
    component: () => import('@/views/ViewTesters'),
    meta: {
      requiresAuth: false
    }
  },

  {
    path: '/reviews/:id',
    redirect: (to) => `/~/reviews/view/${to.params.id}`
  },
  {
    path: '/title/:id/:type',
    redirect: (to) => `/~/title/${to.params.type}/${to.params.id}`
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/ViewNotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
  // Note: scrollBehavior removed - let IonRouterOutlet handle scroll preservation
})

// Gate checks

// navigation guards

router.beforeEach(async (to, from) => {
  // Initialize stores if not already done
  if (!storeAuth) {
    storeAuth = useStoreAuth()
  }
  if (!storeProfile) {
    storeProfile = useStoreProfile()
  }
  if (!storePreCache) {
    storePreCache = useStorePreCache()
  }

  // Pause and clear pre-cache during navigation
  storePreCache.pause()
  storePreCache.clear()

  // A cold load reaches this guard before the session has been restored:
  // App.vue starts the auth store on mount, after the first navigation. A
  // protected page loaded cold — the post-code redirect to one, or a
  // reload — therefore saw no user and sent a signed-in person to the login
  // page. Start the store here and wait for its answer before deciding.
  const needsUser =
    to.meta.requiresAuth ||
    to.meta.requiresAdmin ||
    (to.name === 'auth' && to.query.redirect)
  if (needsUser && !storeAuth.user && !storeAuth.userLoaded) {
    storeAuth.init()
    await untilUserLoaded(storeAuth)
  }

  // Already signed in and asked to sign in again on the way somewhere: go
  // there. The login page does not check for a session itself.
  if (to.name === 'auth' && to.query.redirect && storeAuth.user) {
    const redirect = Array.isArray(to.query.redirect)
      ? to.query.redirect[0]
      : to.query.redirect
    if (redirect && redirect.startsWith('/')) return redirect
  }

  // Check for authentication requirement
  if (to.meta.requiresAuth) {
    if (!storeAuth.user) {
      const isAuthRoute =
        to.name === 'auth' ||
        to.name === 'register' ||
        to.path.startsWith('/auth')
      const isFromAuthRoute =
        from.name === 'auth' ||
        from.name === 'register' ||
        from.path?.startsWith('/auth')

      if (!isFromAuthRoute && from.fullPath && from.fullPath !== '/') {
        sessionStorage.setItem('authRedirectRoute', from.fullPath)
      }

      return { name: 'auth', query: { redirect: to.fullPath } }
    }
  }

  // Check for admin routes
  if (to.meta.requiresAdmin) {
    // Profile may not be hydrated yet on a cold load — currentUserId is
    // persisted, but currentUserIsAdmin isn't and defaults to false. Bounce to
    // a holding page that waits for the profile to load and then retries,
    // rather than wrongly redirecting a real admin out before we know.
    if (!storeProfile.currentUserLoaded) {
      return { name: 'WebAdminBounce' }
    }

    // Loaded and confirmed non-admin: keep them out.
    if (!storeProfile.currentUserIsAdmin) {
      return { name: 'WebIndex' }
    }
  }
})

// Resolves once the auth store has said whether there is a session, or
// after a bound, so a stalled restore still ends in the login page rather
// than a blank one.
const untilUserLoaded = (storeAuth, timeoutMs = 8000) =>
  new Promise((resolve) => {
    if (storeAuth.userLoaded) return resolve()
    let stop = null
    const timer = setTimeout(() => {
      if (stop) stop()
      resolve()
    }, timeoutMs)
    stop = watch(
      () => storeAuth.userLoaded,
      (loaded) => {
        if (!loaded) return
        clearTimeout(timer)
        stop()
        resolve()
      }
    )
  })

// Trigger haptic feedback on navigation (iOS only)
const triggerNavigationHaptic = async () => {
  if (Capacitor.getPlatform() === 'ios') {
    try {
      await Haptics.impact({ style: ImpactStyle.Medium })
    } catch (error) {
      // Haptics not available
    }
  }
}

// Resume pre-caching after navigation completes
router.afterEach((to, from) => {
  // Trigger haptic feedback on navigation (skip initial load)
  if (from.name) {
    triggerNavigationHaptic()
  }

  // Give the page time to render before resuming pre-cache
  setTimeout(() => {
    if (storePreCache) {
      storePreCache.resume()
    }
  }, 500)
})

export default router
